/* ══════════════════════════════════════════════════════════
   AUDIO CAPTURE — standalone audio task flow
   Two-screen orchestrator:
     1. AudioRecorderSurface  (ready / recording / paused / review, multi-clip)
     2. AudioNotesSurface     (notes/addendum, dark themed)
   ══════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import { useAudioRecorder, AudioClip } from './useAudioRecorder';
import { setUnsavedCapture } from '../shared/unsavedWork';
import { AudioRecorderSurface, AudioSurfaceState } from './AudioRecorderSurface';
import { AudioNotesSurface } from './AudioNotesSurface';
import { AUDIO } from '../../../../utils/audioFeedback';
import type { InitialDraft } from '../shared';

/** External step type kept for backwards compatibility with consumers. */
export type AudioStep = 'ready' | 'recording' | 'paused' | 'review' | 'notes';

export interface AudioCaptureProps {
  taskName: string;
  prompt: string;
  onSubmit: (audioBlobs: Blob[], notes: string) => void;
  onSaveDraft: (audioBlobs: Blob[], notes: string) => void;
  onClose: () => void;
  initialNotes?: string;
  initialDraft?: InitialDraft;
  /** Fired on unmount IFF the user modified clips this session (deleted or
      recorded new) but did NOT explicitly Save Draft or Submit. Parent
      should delete the IndexedDB draft for this task so the card stops
      showing "Resume" and the next entry is fresh. Matches the user
      model where persistence requires an explicit Save Draft tap. */
  onDiscardDraft?: () => void;
}

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const BackArrowIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const AudioCapture: React.FC<AudioCaptureProps> = ({
  prompt,
  onSubmit,
  onSaveDraft,
  onClose,
  initialNotes,
  initialDraft,
  onDiscardDraft,
}) => {
  const hasDraftMedia = !!initialDraft?.mediaBlobs.length;
  /** Phase tracks whether we're in the capture surface or the notes surface. */
  const [phase, setPhase] = useState<'capture' | 'notes'>('capture');
  /** Recorder state within the capture surface. */
  const [state, setState] = useState<AudioSurfaceState>(hasDraftMedia ? 'review' : 'ready');
  const recorder = useAudioRecorder();
  const prevClipCount = useRef(0);
  const injectedRef = useRef(false);
  /** Flipped true when the user makes a clip-list change this session
      (new recording or deletion) — drives the discard-on-exit logic. */
  const wasModifiedRef = useRef(false);
  /** Flipped true once handleSubmit or handleSaveDraft has fired this
      session. If the user explicitly saved, we don't discard on unmount. */
  const didExplicitSaveRef = useRef(false);

  /* Recorded clips not yet saved → page-level leave nudge */
  useEffect(() => {
    setUnsavedCapture(recorder.clips.length > 0);
    return () => setUnsavedCapture(false);
  }, [recorder.clips.length]);

  /* Inject draft clips on mount (if returning from a saved draft). */
  useEffect(() => {
    if (hasDraftMedia && !injectedRef.current) {
      injectedRef.current = true;
      const clips: AudioClip[] = initialDraft!.mediaBlobs.map((blob, i) => ({
        id: `draft-${i}`,
        blob,
        url: URL.createObjectURL(blob),
        duration: initialDraft!.mediaDurations?.[i] ?? 0,
      }));
      recorder.injectClips(clips);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* If all clips deleted during review, slip back to ready.
     Only fires on >0 → 0 transitions, never on initial mount. */
  useEffect(() => {
    if (state === 'review' && recorder.clips.length === 0 && prevClipCount.current > 0) {
      setState('ready');
    }
    prevClipCount.current = recorder.clips.length;
  }, [state, recorder.clips.length]);

  const handleStart = async () => {
    AUDIO.tap();
    await recorder.startRecording();
    AUDIO.start();
    setState('recording');
  };

  const handlePause = () => {
    AUDIO.tap();
    recorder.pauseRecording();
    setState('paused');
  };

  const handleResume = () => {
    AUDIO.tap();
    recorder.resumeRecording();
    setState('recording');
  };

  const handleStop = (opts?: { discard?: boolean }) => {
    AUDIO.stop();
    recorder.stopRecording(opts);
    // On discard, don't transition to review — there's no new clip to review.
    // Stay on the ready surface if no prior clips, else show the existing ones.
    if (opts?.discard) {
      setState(recorder.clips.length > 0 ? 'review' : 'ready');
    } else {
      // A new clip was actually captured — modification flag for discard-on-exit.
      wasModifiedRef.current = true;
      setState('review');
    }
  };

  /** Intercept deleteClip so we can flag a session modification. */
  const handleDeleteClip = (clipId: string) => {
    wasModifiedRef.current = true;
    recorder.deleteClip(clipId);
  };

  /** Retry the recorder after a getUserMedia failure: clear the error
      first so the surface re-renders the normal recording UI even if
      `startRecording` rejects again. */
  const handleRetry = async () => {
    recorder.clearError();
    await handleStart();
  };

  const handleContinueFromCapture = () => {
    AUDIO.tap();
    setPhase('notes');
  };

  /** "+ Record another": go back to the ready surface with clips preserved.
      The user then taps the mic to actually start the new clip — gives them
      a moment to compose rather than starting recording mid-tap. */
  const handleRecordAnother = () => {
    AUDIO.tap();
    setState('ready');
  };

  const handleBackFromNotes = () => {
    AUDIO.tap();
    setPhase('capture');
  };

  /* Clips are handed over as SEPARATE blobs, never concatenated. Each clip is
     a complete WebM container with its own header and a timeline starting at
     zero; `new Blob([clipA, clipB])` byte-appends them into a file that every
     player stops reading at the end of clip A (and STT only transcribes that
     first segment). uploadManager already uploads a Blob[] as one file per
     clip, exactly as photo and video takes do. */
  const handleSubmit = (notes: string) => {
    if (recorder.clips.length > 0) {
      AUDIO.success();
      didExplicitSaveRef.current = true;
      onSubmit(recorder.clips.map(c => c.blob), notes);
    }
  };

  const handleSaveDraft = (notes: string) => {
    didExplicitSaveRef.current = true;
    onSaveDraft(recorder.clips.map(c => c.blob), notes);
  };

  /* Discard-on-exit: when this component unmounts without an explicit save,
     and the user made any clip-list changes, ask the parent to drop the
     IndexedDB draft. Matches the user model that persistence requires an
     explicit Save Draft. Untouched drafts are preserved (the "Resume" path
     for glancing at a prior session). */
  useEffect(() => {
    return () => {
      if (wasModifiedRef.current && !didExplicitSaveRef.current) {
        onDiscardDraft?.();
      }
    };
    // We want this cleanup tied to component unmount only, not prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'notes') {
    return (
      <AudioNotesSurface
        clips={recorder.clips}
        initialNotes={initialNotes}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
        headerLeft={
          <button
            className="ars-close"
            onClick={handleBackFromNotes}
            aria-label="Back to recording"
            type="button"
          >
            <BackArrowIcon />
          </button>
        }
      />
    );
  }

  return (
    <AudioRecorderSurface
      state={state}
      prompt={prompt}
      duration={recorder.duration}
      audioLevel={recorder.audioLevel}
      frequencyData={recorder.frequencyData}
      clips={recorder.clips}
      allowMultipleClips
      onStart={handleStart}
      onPause={handlePause}
      onResume={handleResume}
      onStop={handleStop}
      onDeleteClip={handleDeleteClip}
      onContinue={handleContinueFromCapture}
      onRecordAnother={handleRecordAnother}
      error={recorder.error}
      onRetry={handleRetry}
      wasAutoPaused={recorder.wasAutoPaused}
      headerLeft={
        <button
          className="ars-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <CloseIcon />
        </button>
      }
    />
  );
};
